import { useEffect, useRef, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { bulkChangeStatus } from "../api";
import { confermaBonificoKery } from "../bonificoKeryApi";
import type { PaymentState } from "../types";
import { ActionButton } from "./ActionButton";
import { IconCheck } from "./icons";

type OrdineBonifico = {
  raw?: { source?: unknown; payment_method?: unknown } | null;
  paymentState?: PaymentState;
};

function sorgente(order: OrdineBonifico): string {
  const rawSource = order.raw?.source;
  return (typeof rawSource === "string" ? rawSource : "").toUpperCase();
}

/** Ordine del NOSTRO negozio pagato con bonifico e ancora in attesa
 *  dell'accredito: card gialla, stato «Attesa bonifico», resta tra i «Da
 *  evadere». Nato per WooCommerce (2026-07-02); dal 29/10/2026 anche Kery su
 *  Shopify (KR2006). I marketplace in attesa di pagamento restano fuori: non
 *  sono bonifici nostri. Firma strutturale come orderMarketplaceLogo. */
/** Come il bonifico arriva scritto: WooCommerce in italiano, Shopify col nome
 *  del suo metodo manuale («Bank Deposit», KR2006 e KR2008). */
const NOMI_BONIFICO = ["bonifico", "bank deposit"];

export function isBonificoDaVerificare(order: OrdineBonifico): boolean {
  const source = sorgente(order);
  const rawMethod = order.raw?.payment_method;
  const method = (typeof rawMethod === "string" ? rawMethod : "").toLowerCase();
  return (
    (source.startsWith("WOO") || source === "SHOPIFY") &&
    NOMI_BONIFICO.some((nome) => method.includes(nome)) &&
    order.paymentState === "awaiting_payment"
  );
}

/** Su Kery il bonifico si conferma su Shopify: il tasto «Bonifico arrivato»
 *  lo segna pagato li' (Davide 2/11, KR2008), e lo fa anche «Segna come
 *  pagato» su Shopify, che il giro ordini riporta in OM. Mai via Poleepo. */
export function bonificoSiConfermaSuShopify(order: OrdineBonifico): boolean {
  return sorgente(order) === "SHOPIFY";
}

/** Bottone "Bonifico arrivato": conferma in 2 step (arm → conferma) e porta
 *  l'ordine a PAYMENT_ACCEPTED via Poleepo (PUT /orders/{id}/status), che
 *  propaga il pagamento accettato al canale WooCommerce. Riusa l'endpoint
 *  bulk-change-status gia' in produzione (legacy parity #11): un solo ref.
 *  Collaudo 2026-07-03: da PENDING_PAYMENT Poleepo ammette SOLO la
 *  transizione PAYMENT_ACCEPTED (verificato con GET /orders/{id}/status/preview);
 *  il vecchio target WORK_IN_PROGRESS rispondeva sempre HTTP 400. */
export function ConfermaBonificoButton({
  order,
  onDone,
  compact = false,
}: {
  /** Un ordine Kery (Shopify) si segna pagato su Shopify, mai via Poleepo. */
  order: OrdineBonifico & { id: string };
  onDone?: () => void;
  /** Colonna Azioni della card lista (Piano 3 Task 3): icona 26px invece
   *  del pill testuale. Stesso state-machine/handler, cambia solo il
   *  markup. Default false = rendering invariato (usato nella griglia dei
   *  tasti della pagina ordine, dove il testo resta). */
  compact?: boolean;
}) {
  const orderRef = order.id;
  const kery = bonificoSiConfermaSuShopify(order);
  const [step, setStep] = useState<"idle" | "armed" | "busy" | "done">("idle");
  const [error, setError] = useState<string | null>(null);
  const disarmTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (disarmTimerRef.current !== null) {
        window.clearTimeout(disarmTimerRef.current);
        disarmTimerRef.current = null;
      }
    };
  }, []);

  async function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (step === "busy" || step === "done") return;
    if (step === "idle") {
      // Primo click: arma la conferma (azione outward-facing: cambia lo
      // stato dell'ordine anche sul sito → mai one-click).
      setError(null);
      setStep("armed");
      if (disarmTimerRef.current !== null) window.clearTimeout(disarmTimerRef.current);
      disarmTimerRef.current = window.setTimeout(() => {
        disarmTimerRef.current = null;
        setStep((current) => (current === "armed" ? "idle" : current));
      }, 5000);
      return;
    }
    if (disarmTimerRef.current !== null) {
      window.clearTimeout(disarmTimerRef.current);
      disarmTimerRef.current = null;
    }
    setStep("busy");
    try {
      if (kery) {
        await confermaBonificoKery(orderRef);
      } else {
        const result = await bulkChangeStatus([orderRef], "PAYMENT_ACCEPTED");
        if (!result || result.updated < 1) {
          throw new Error(result?.errors?.[0]?.error || "Aggiornamento stato non riuscito");
        }
      }
      setStep("done");
      onDone?.();
    } catch (err) {
      setError(prettifyApiError(err));
      setStep("idle");
    }
  }

  // Nome accessibile completo (aria-label): idle "Bonifico arrivato" (i test
  // by-role lo cercano), armato "Confermi?…". La micro-etichetta VISIBILE del
  // tasto card resta breve ("Bonifico"/"Confermi?"), lo spazio non basta al
  // testo lungo.
  const label =
    step === "armed"
      ? kery
        ? "Confermi? → Pagato anche su Shopify"
        : "Confermi? → Pagamento accettato"
      : step === "busy"
        ? "Confermo…"
        : step === "done"
          ? "✓ Pagamento accettato"
          : "Bonifico arrivato";

  if (compact) {
    const microLabel =
      step === "armed"
        ? "Confermi?"
        : step === "busy"
          ? "Invio…"
          : step === "done"
            ? "Fatto"
            : "Bonifico";
    return (
      <span className="inline-flex items-start gap-1.5 shrink-0">
        <ActionButton
          tone={step === "done" ? "emerald" : "amber"}
          icon={
            <IconCheck className="w-[13px] h-[13px] fill-none stroke-current stroke-[2.4] [stroke-linecap:round] [stroke-linejoin:round]" />
          }
          label={microLabel}
          ariaLabel={label}
          title={label}
          busy={step === "busy"}
          disabled={step === "done"}
          onClick={(e) => void handleClick(e)}
        />
        {error && (
          <span
            role="alert"
            title={error}
            className="text-[11px] text-red max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {error}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 shrink-0">
      <button
        type="button"
        data-no-open
        disabled={step === "busy" || step === "done"}
        aria-label={step === "armed" ? `Confermi bonifico arrivato ordine ${orderRef}` : undefined}
        title="Segna il bonifico come ricevuto: ordine → Pagamento accettato (propagato a WooCommerce via Poleepo)"
        onClick={(e) => void handleClick(e)}
        className={[
          "inline-flex items-center gap-[6px] rounded-sm border px-[10px] py-1.5 text-[12px] font-medium shrink-0 disabled:opacity-60",
          step === "armed"
            ? "border-[#CA8A04] bg-[#EAB308] text-white hover:bg-[#CA8A04]"
            : "border-[#EAB308] bg-[#FEF9C3] text-[#854D0E] hover:bg-[#FEF08A]",
        ].join(" ")}
      >
        {label}
      </button>
      {error && (
        <span
          role="alert"
          title={error}
          className="text-[11px] text-red max-w-[240px] overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {error}
        </span>
      )}
    </span>
  );
}
